import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import {
  BulkImportError,
  bulkImportAffiniaUsersSsoConfig,
  downloadAffiniaUsersSsoConfigTemplate,
  type AffiniaUsersSsoConfigBulkImportResult,
} from "@/services/affiniaUsersSsoConfigApi";

const UPLOAD_MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_UPLOAD_EXTENSIONS = [".csv", ".xlsx"] as const;

function validateUploadFile(file: File): string | null {
  const name = file.name.toLowerCase();
  if (!ALLOWED_UPLOAD_EXTENSIONS.some((ext) => name.endsWith(ext))) {
    return "Only .csv or .xlsx files can be uploaded.";
  }
  if (file.size > UPLOAD_MAX_BYTES) {
    return `The file is larger than ${UPLOAD_MAX_BYTES / (1024 * 1024)} MB.`;
  }
  return null;
}

type UploadState = "idle" | "uploading" | "success" | "error";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function AffiniaUsersSsoConfigImportModal({ open, onOpenChange }: Props) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploadState, setUploadState] = useState<UploadState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorProblems, setErrorProblems] = useState<string[]>([]);
  const [result, setResult] = useState<AffiniaUsersSsoConfigBulkImportResult | null>(null);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

  // Fresh slate every time the modal opens -- a leftover success panel from
  // the previous session would mislead the admin on their next upload.
  useEffect(() => {
    if (open) {
      setFile(null);
      setUploadState("idle");
      setErrorMessage(null);
      setErrorProblems([]);
      setResult(null);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: bulkImportAffiniaUsersSsoConfig,
    onMutate: () => {
      setUploadState("uploading");
      setErrorMessage(null);
      setErrorProblems([]);
      setResult(null);
    },
    onSuccess: (data) => {
      setResult(data);
      setUploadState("success");
    },
    onError: (e: unknown) => {
      if (e instanceof BulkImportError) {
        setErrorMessage(e.message);
        setErrorProblems(e.problems);
      } else {
        setErrorMessage((e as Error)?.message ?? "Bulk import failed.");
        setErrorProblems([]);
      }
      setUploadState("error");
    },
  });

  const handleChooseFile = () => {
    if (uploadState === "uploading") return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files?.[0];
    e.target.value = "";
    if (!chosen) return;
    const problem = validateUploadFile(chosen);
    if (problem) {
      setErrorMessage(problem);
      setErrorProblems([]);
      setUploadState("error");
      setFile(null);
      return;
    }
    // Picking a new file clears any previous server-side error so the admin
    // sees fresh feedback for the new attempt.
    setFile(chosen);
    setErrorMessage(null);
    setErrorProblems([]);
    setUploadState("idle");
    setResult(null);
  };

  const handleUpload = () => {
    if (!file || uploadState === "uploading") return;
    mutation.mutate(file);
  };

  const handleDownloadTemplate = async () => {
    if (isDownloadingTemplate) return;
    setIsDownloadingTemplate(true);
    try {
      await downloadAffiniaUsersSsoConfigTemplate();
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Couldn't download the template",
        description: (e as Error).message,
      });
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    // Block closing while an upload is in flight; the backend write cannot be
    // cancelled from here and the admin should see the result.
    if (!next && uploadState === "uploading") return;
    onOpenChange(next);
  };

  const canUpload = file !== null && uploadState !== "uploading";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Bulk import users to the Affinia SSO config</DialogTitle>
          <DialogDescription>
            Upload a .csv or .xlsx file to add new users to the Affinia
            users SSO config. These users will be allowed to sign in to
            Prism via SSO on their first visit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="rounded-md border border-border bg-muted/40 p-3">
            <p className="mb-2 font-medium text-foreground">Rules</p>
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>
                Required columns match the template. Download it below if
                you're unsure of the exact headings.
              </li>
              <li>
                Mandatory values: <strong>Email</strong>,{" "}
                <strong>Name</strong>, <strong>Role</strong>,{" "}
                <strong>Grade</strong>.
              </li>
              <li>Each email may appear only once in the file.</li>
              <li>
                An email already in the config will be skipped (not an error).
                To update an existing user's role or attributes, edit them
                from the Users table instead.
              </li>
              <li>Max file size: 5 MB.</li>
            </ul>
          </div>

          <div className="flex items-center justify-between gap-2 rounded-md border border-border p-3">
            <div className="min-w-0">
              <p className="text-foreground">Need the correct column layout?</p>
              <p className="text-xs text-muted-foreground">
                Downloads an empty .xlsx with the required headings. Save-as
                in Excel if you'd rather upload a .csv.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 shrink-0"
              onClick={handleDownloadTemplate}
              disabled={isDownloadingTemplate}
            >
              {isDownloadingTemplate ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {isDownloadingTemplate ? "Downloading…" : "Download template"}
            </Button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx"
            className="hidden"
            onChange={handleFileSelected}
          />

          <div className="flex items-center justify-between gap-2 rounded-md border border-dashed border-border p-3">
            <div className="flex min-w-0 items-center gap-2">
              <FileSpreadsheet className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                {file ? (
                  <>
                    <p className="truncate text-foreground">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatBytes(file.size)}
                    </p>
                  </>
                ) : (
                  <p className="text-muted-foreground">No file chosen.</p>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 shrink-0"
              onClick={handleChooseFile}
              disabled={uploadState === "uploading"}
            >
              <Upload className="h-4 w-4" />
              {file ? "Choose another" : "Choose file"}
            </Button>
          </div>

          {uploadState === "error" && errorMessage && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-destructive">
              <div className="mb-2 flex items-center gap-2 font-medium">
                <AlertTriangle className="h-4 w-4" />
                File rejected — nothing was saved
              </div>
              {errorProblems.length > 0 ? (
                <ScrollArea className="max-h-56 pr-2">
                  <ul className="list-disc space-y-1 pl-5 text-sm">
                    {errorProblems.map((problem, index) => (
                      <li key={index}>{problem}</li>
                    ))}
                  </ul>
                </ScrollArea>
              ) : (
                <p className="text-sm">{errorMessage}</p>
              )}
            </div>
          )}

          {uploadState === "success" && result && (
            <div className="space-y-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200">
              <div className="flex items-center gap-2 font-medium">
                <CheckCircle2 className="h-4 w-4" />
                {result.inserted_count === 0
                  ? "No new users added"
                  : `${result.inserted_count} ${
                      result.inserted_count === 1 ? "user" : "users"
                    } added to the Affinia SSO config`}
              </div>

              {result.inserted_count > 0 && (
                <EmailBreakdown
                  label="Added:"
                  emails={result.inserted_emails}
                />
              )}

              {result.skipped_count > 0 && (
                <EmailBreakdown
                  label="Skipped (already in config):"
                  emails={result.skipped_emails}
                />
              )}

              {result.duplicate_count > 0 && (
                <EmailBreakdown
                  label="Duplicates in the file (first kept):"
                  emails={result.duplicate_emails}
                />
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={uploadState === "uploading"}
          >
            {uploadState === "success" ? "Close" : "Cancel"}
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!canUpload}
            className="gap-2"
          >
            {uploadState === "uploading" ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading…
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Upload
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface EmailBreakdownProps {
  label: string;
  emails: string[];
}

function EmailBreakdown({ label, emails }: EmailBreakdownProps) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium">{label}</p>
      <ScrollArea className="max-h-32 pr-2">
        <ul className="list-disc space-y-0.5 pl-5 text-sm">
          {emails.map((email) => (
            <li key={email}>{email}</li>
          ))}
        </ul>
      </ScrollArea>
    </div>
  );
}
